import { t } from "i18next";

import { errors } from "../constants";
import { IExtendableDto, SimpleResult } from "../dto";
import { Api, getCompanyId, isNumber, Log } from "../helpers";
import {
  IReportApiDto,
  IReportCriteriaApi,
  IReportSaveRequestDto,
  IReportSaveResponseDto,
  IReportTemplateItem,
  IReportTemplateItemBase,
  ISearchCriteria,
} from "../models";

export class ReportsApi {
  static async fetchReportData(
    payload?: IReportCriteriaApi,
    abortController?: AbortController,
  ): Promise<SimpleResult<IReportApiDto> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/reports/report-data`;
    const response = await Api.post<SimpleResult<IReportApiDto> | undefined>(
      requestUrl,
      payload,
      abortController,
    );

    return response?.data;
  }

  static async fetchReportDataCSV(
    language: string,
    payload?: IReportCriteriaApi,
    abortController?: AbortController,
  ): Promise<SimpleResult<Blob> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/reports/report-data-csv?language=${language}`;

    const response = await Api.post<Blob | undefined>(
      requestUrl,
      payload,
      abortController,
      undefined,
      true,
      undefined,
      "blob",
    );

    return new SimpleResult({ data: response?.data, success: true });
  }

  static async count(
    payload: ISearchCriteria<IExtendableDto>,
  ): Promise<number> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/reports/count`;
    const response = await Api.post<boolean>(requestUrl, payload);

    if (isNumber(response?.data)) {
      return response?.data;
    }

    //todo translation
    Log.error(
      t(errors.reports.wrongResponseTypeFromReportCount, {
        dataType: typeof response?.data,
      }),
      response?.data,
    );

    return 0;
  }

  static async getReportsTemplates(): Promise<
    SimpleResult<IReportTemplateItemBase[]> | undefined
  > {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/reports/templates`;
    const response = await Api.get<
      SimpleResult<IReportTemplateItemBase[]> | undefined
    >(requestUrl);

    return response?.data;
  }

  static async getReportsMenuItems(): Promise<
    SimpleResult<IReportTemplateItem[]> | undefined
  > {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/reports/menu-items`;
    const response = await Api.get<
      SimpleResult<IReportTemplateItem[]> | undefined
    >(requestUrl);

    return response?.data;
  }

  static async addOrUpdate(
    payload: IReportSaveRequestDto,
    abortController?: AbortController,
  ): Promise<SimpleResult<IReportSaveResponseDto> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/reports/add-or-update`;
    const response = await Api.post<
      SimpleResult<IReportSaveResponseDto> | undefined
    >(requestUrl, payload, abortController);

    return response?.data;
  }

  static async delete(
    reportId: number,
  ): Promise<SimpleResult<number> | undefined> {
    const companyId = getCompanyId();
    const response = await Api.delete<SimpleResult<number>>(
      `/api/v1/core/c/${companyId}/reports/${reportId}`,
    );

    return response?.data;
  }
}
